import React from 'react'
import styled from '@emotion/styled'
import { Grid } from '@mui/material'
import { useRecoilValue } from 'recoil'
import { Flex } from 'src/components/flex-containers'
import { LoadablePanel, TitleWrapper } from 'src/components/sidebar/panel'
import { Typography } from '@mui/material'
import { Colors, Shapes } from 'src/components/theme'
import { CoverWithCloseButton } from 'src/components/sidebar-panels/custom-area-info/cover-with-close-button'
import { SavedIn } from 'src/components/saved-in'

import * as atoms from 'src/atoms/custom-area'
import * as userListsAtoms from 'src/atoms/user-lists'
import { prettyNumber } from 'src/utils/number-format'

interface FactProps {
  readonly label: string
  readonly value: string
  readonly hidden?: boolean
}

type FactLabelProps = {
  children?: React.ReactNode
}

type FactValueProps = {
  children?: React.ReactNode
}

export const fmt = (num: number | undefined, suffix: string): string => {
  return num ? `${num.toFixed(2)} ${suffix}` : '--'
}

const Fact: React.FC<FactProps> = ({ label, value, hidden }) => {
  if (hidden) {
    return null
  }

  return (
    <Grid container paddingY={'2px'}>
      <FactLabel>{label}</FactLabel>
      <FactValue>{value}</FactValue>
    </Grid>
  )
}

const FactLabel = (props: FactLabelProps) => {
  const { children } = props
  return (
    <Grid item xs={6} color={Colors.neutral500}>
      {children}
    </Grid>
  )
}

const FactValue = (props: FactValueProps) => {
  const { children } = props
  return (
    <Grid item xs={6} color={Colors.neutral700}>
      {children}
    </Grid>
  )
}

const FactsWrapper = styled(Flex)`
  padding-inline: ${Shapes.spacing[0]}px;
`
export const CustomAreaInfoFacts: React.FC = () => {
  const area = useRecoilValue(atoms.areaSqMi)
  const perimeter = useRecoilValue(atoms.perimeterMiles)
  const radius = useRecoilValue(atoms.radiusMiles)
  const centroid = useRecoilValue(atoms.centroid)
  // show lat, lng though we store things as [lng, lat]
  const fmtCentroid =
    centroid
      ?.map((num) => num.toFixed(6))
      .reverse()
      .join(', ') ?? '--'
  return (
    <FactsWrapper>
      <Grid container>
        <Fact label="Total Area" value={`${prettyNumber(area)} sq.mi.`} />
        <Fact label="Perimeter" value={fmt(perimeter, 'mi.')} />
        <Fact label="Radius" value={fmt(radius, 'mi.')} hidden={!radius} />
        <Fact label="Center Point" value={fmtCentroid} />
      </Grid>
    </FactsWrapper>
  )
}

const Header = styled(TitleWrapper)`
  margin-top: ${Shapes.spacing[5]}px;
  margin-bottom: ${Shapes.spacing[2]}px;
  position: relative;
  color: black !important;
  h3 {
    line-height: 22px;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }
`

const TitleWithClose: React.FC = () => {
  const userListItemDetails = useRecoilValue(userListsAtoms.userListItemDetails)
  return (
    <Header>
      <Typography variant="header3" color="black">
        {userListItemDetails && 'name' in userListItemDetails ? userListItemDetails.name : 'Custom Area'}
      </Typography>
    </Header>
  )
}

const StyledSavedInWrapper = styled.div`
  padding-inline: ${Shapes.spacing[4]}px;
  z-index: 1;
`

export const CustomAreaInfo: React.FC = () => {
  return (
    <>
      <CoverWithCloseButton />
      <TitleWithClose />
      <StyledSavedInWrapper>
        <SavedIn />
      </StyledSavedInWrapper>
      <LoadablePanel>
        <CustomAreaInfoFacts />
      </LoadablePanel>
    </>
  )
}
